/**
 * A tool call waiting on a decision, from the prototype's approval card (lines
 * ~120-134). Pending gets the primary border, Allow and Deny; answered
 * settles to a quiet "Allowed" or "Denied" line instead.
 */

import { useState } from 'react';
import { answerCard, ApiError, type CardAnswer } from '../../api';
import { ds } from '../../ds';
import { Ph } from '../ui';
import type { CardProps } from './types';

export function ApprovalCard({ chatId, message, onChange }: CardProps) {
  const { Button } = ds();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (message.body.kind !== 'approval') return null;
  const { title, app, state } = message.body;
  const pending = state === 'pending';

  const act = (approval: NonNullable<CardAnswer['approval']>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    answerCard(chatId, message.id, { approval })
      .then(onChange)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'That did not go through. Try again.')
      )
      .finally(() => setBusy(false));
  };

  return (
    <div
      style={{
        background: 'var(--surface-card)',
        border: `1px solid ${pending ? 'var(--primary)' : 'transparent'}`,
        borderRadius: 'var(--radius-app-card)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 30,
            height: 30,
            flex: 'none',
            borderRadius: 8,
            background: 'var(--primary-soft)',
            color: 'var(--primary)',
            display: 'grid',
            placeItems: 'center'
          }}
        >
          <Ph name="cube" size={16} />
        </span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>{title}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{app}</span>
        </div>
        {state === 'allowed' ? (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Ph name="check" size={13} />
            Allowed
          </span>
        ) : state === 'denied' ? (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Denied</span>
        ) : null}
      </div>

      {pending ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button variant="accent" density="app" size="sm" disabled={busy} onClick={() => act('allowed')}>
            Allow
          </Button>
          <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={() => act('denied')}>
            Deny
          </Button>
        </div>
      ) : null}

      {error ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error}</span> : null}
    </div>
  );
}
